import React4, { useState, useCallback, useMemo, useEffect } from 'react';
import { defaultThemeMap as defaultThemeMap$1, createStitches } from '@stitches/react';
import { deepMerge, clsx } from '@atom/utils';
import { SSRProvider } from '@react-aria/ssr';
import { OverlayProvider } from '@react-aria/overlays';

var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// theme/colors.ts
var colors_exports = {};
__export(colors_exports, {
  black: () => black,
  green00: () => green00,
  green01: () => green01,
  green02: () => green02,
  green03: () => green03,
  green04: () => green04,
  green05: () => green05,
  green06: () => green06,
  green07: () => green07,
  green08: () => green08,
  green09: () => green09,
  green10: () => green10,
  grey00: () => grey00,
  grey01: () => grey01,
  grey02: () => grey02,
  grey03: () => grey03,
  grey04: () => grey04,
  grey05: () => grey05,
  grey06: () => grey06,
  grey07: () => grey07,
  grey08: () => grey08,
  grey09: () => grey09,
  grey10: () => grey10,
  grey11: () => grey11,
  grey12: () => grey12,
  purple00: () => purple00,
  purple01: () => purple01,
  purple02: () => purple02,
  purple03: () => purple03,
  purple04: () => purple04,
  purple05: () => purple05,
  purple06: () => purple06,
  purple07: () => purple07,
  purple08: () => purple08,
  purple09: () => purple09,
  purple10: () => purple10,
  red00: () => red00,
  red01: () => red01,
  red02: () => red02,
  red03: () => red03,
  red04: () => red04,
  red05: () => red05,
  red06: () => red06,
  red07: () => red07,
  red08: () => red08,
  red09: () => red09,
  red10: () => red10,
  white: () => white,
  yellow00: () => yellow00,
  yellow01: () => yellow01,
  yellow02: () => yellow02,
  yellow03: () => yellow03,
  yellow04: () => yellow04,
  yellow05: () => yellow05,
  yellow06: () => yellow06,
  yellow07: () => yellow07,
  yellow08: () => yellow08,
  yellow09: () => yellow09,
  yellow10: () => yellow10
});
var red00 = "#FDECEF";
var red01 = "#FAC7CE";
var red02 = "#F7A1AE";
var red03 = "#F47B8E";
var red04 = "#F15F75";
var red05 = "#EF435D";
var red06 = "#D73C54";
var red07 = "#BF364A";
var red08 = "#A72F41";
var red09 = "#8F2838";
var red10 = "#78212E";
var green00 = "#E5F9F3";
var green01 = "#B2EEDA";
var green02 = "#80E3C2";
var green03 = "#4CD7A9";
var green04 = "#26CF96";
var green05 = "#00C684";
var green06 = "#00B277";
var green07 = "#009E6A";
var green08 = "#008B5C";
var green09 = "#00774F";
var green10 = "#006342";
var yellow00 = "#FFFAEB";
var yellow01 = "#FFEFC2";
var yellow02 = "#FFE499";
var yellow03 = "#FFD970";
var yellow04 = "#FFD152";
var yellow05 = "#FFC933";
var yellow06 = "#E5B52E";
var yellow07 = "#CCA129";
var yellow08 = "#B38D24";
var yellow09 = "#99791F";
var yellow10 = "#99791F";
var purple00 = "#ECE4F6";
var purple01 = "#E1D5F1";
var purple02 = "#C5AFE4";
var purple03 = "#A989D7";
var purple04 = "#8D63CA";
var purple05 = "#7E4FC3";
var purple06 = "#7140BA";
var purple07 = "#6639A7";
var purple08 = "#5A3394";
var purple09 = "#4F2C81";
var purple10 = "#44266E";
var grey00 = "#F7F7F7";
var grey01 = "#E5E5E5";
var grey02 = "#D4D4D4";
var grey03 = "#C3C3C3";
var grey04 = "#B7B7B7";
var grey05 = "#AAAAAA";
var grey06 = "#999999";
var grey07 = "#888888";
var grey08 = "#777777";
var grey09 = "#666666";
var grey10 = "#555555";
var grey11 = "#444444";
var grey12 = "#333333";
var white = "#FFFFFF";
var black = "#000000";

// theme/common.ts
var defaultTokens = {
  fonts: {
    sans: "-apple-system, BlinkMacSystemFont, 'Segoe UI','Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans','Helvetica Neue', sans-serif;",
    mono: "Menlo, Monaco, 'Lucida Console', 'Liberation Mono', 'DejaVu Sans Mono', 'Bitstream Vera Sans Mono','Courier New', monospace;"
  },
  fontSizes: {
    1: "12px",
    2: "13px",
    3: "15px",
    4: "17px",
    5: "19px",
    6: "21px",
    7: "27px",
    8: "35px",
    9: "59px"
  },
  fontWeights: {
    hairline: 100,
    thin: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900
  },
  letterSpacings: {
    tighter: "-0.05em",
    tight: "-0.025em",
    normal: "0",
    wide: "0.025em",
    wider: "0.05em",
    widest: "0.1em"
  },
  space: {
    1: "5px",
    2: "10px",
    3: "15px",
    4: "20px",
    5: "25px",
    6: "35px",
    7: "45px",
    8: "55px",
    9: "65px"
  },
  sizes: {
    1: "5px",
    2: "10px",
    3: "15px",
    4: "20px",
    5: "25px",
    6: "35px",
    7: "45px",
    8: "55px",
    9: "65px"
  },
  radii: {
    1: "4px",
    2: "6px",
    3: "8px",
    4: "12px",
    round: "50%",
    pill: "9999px"
  },
  zIndices: {
    1: "100",
    2: "200",
    3: "300",
    4: "400",
    5: "500",
    10: "1000",
    max: "9999"
  },
  borderWeights: {
    light: "1px",
    normal: "2px",
    bold: "3px",
    extrabold: "4px",
    black: "5px"
  },
  transitions: {
    default: "all 250ms ease",
    button: "background 0.25s ease 0s, color 0.25s ease 0s, border-color 0.25s ease 0s, box-shadow 0.25s ease 0s, transform 0.25s ease 0s, opacity 0.25s ease 0s",
    avatar: "box-shadow 0.25s ease 0s, opacity 0.25s ease 0s",
    link: "opacity 0.25s ease 0s, background 0.25s ease 0s",
    card: "transform 0.25s ease 0s, filter 0.25s ease 0s, box-shadow 0.25s ease 0s",
    dropdownItem: "background 0.25s ease, transform 0.25s ease, color 0.15s ease, box-shadow 0.25s ease 0s"
  },
  breakpoints: {
    sm: "576px",
    md: "768px",
    lg: "992px",
    xl: "1200px",
    xxl: "1400px"
  }
};
var defaultColors = colors_exports;
var defaultMedia = {
  sm: `(min-width: ${defaultTokens.breakpoints.sm})`,
  md: `(min-width: ${defaultTokens.breakpoints.md})`,
  lg: `(min-width: ${defaultTokens.breakpoints.lg})`,
  xl: `(min-width: ${defaultTokens.breakpoints.xl})`,
  xxl: `(min-width: ${defaultTokens.breakpoints.xxl})`,
  smMax: `(max-width: ${defaultTokens.breakpoints.sm})`,
  mdMax: `(max-width: ${defaultTokens.breakpoints.md})`,
  lgMax: `(max-width: ${defaultTokens.breakpoints.lg})`,
  xlMax: `(max-width: ${defaultTokens.breakpoints.xl})`,
  xxlMax: `(max-width: ${defaultTokens.breakpoints.xxl})`,
  motion: "(prefers-reduced-motion: reduce)",
  safari: "not all and (min-resolution:.001dpcm)",
  hover: "(any-hover: hover)",
  dark: "(prefers-color-scheme: dark)",
  light: "(prefers-color-scheme: light)"
};
var defaultUtils = {
  truncateText: (value) => ({
    maxWidth: value,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  })
};
var defaultThemeMap = {
  ...defaultThemeMap$1,
  borderWidth: "borderWeights"
};
var common_default = {
  prefix: "atom-web",
  theme: {
    ...defaultTokens,
    colors: defaultColors,
    shadows: {},
    dropShadows: {}
  },
  media: defaultMedia,
  utils: defaultUtils,
  themeMap: defaultThemeMap
};

// theme/light.ts
var light_default = {
  colors: {
    background: "$white",
    foreground: "$black",
    backgroundContrast: "$white",
    ...colors_exports,
    text: "$gray09",
    link: "$purple06",
    codeLight: "$red01",
    code: "$red06",
    selection: "$purple06"
  },
  shadows: {
    xs: "0 2px 8px 1px rgb(104 112 118 / 0.07), 0 1px 1px -1px rgb(104 112 118 / 0.04)",
    sm: "0 2px 8px 2px rgb(104 112 118 / 0.07), 0 2px 4px -1px rgb(104 112 118 / 0.04)",
    md: "0 12px 20px 6px rgb(104 112 118 / 0.08)",
    lg: "0 12px 34px 6px rgb(104 112 118 / 0.18)",
    xl: "0 25px 65px 0px rgb(104 112 118 / 0.35)"
  },
  dropShadows: {
    xs: "drop-shadow(0 2px 4px rgb(104 112 118 / 0.07)) drop-shadow(0 1px 1px rgb(104 112 118 / 0.04))",
    sm: "drop-shadow(0 2px 8px rgb(104 112 118 / 0.07)) drop-shadow(0 2px 4px rgb(104 112 118 / 0.04))",
    md: "drop-shadow(0 4px 12px rgb(104 112 118 / 0.08)) drop-shadow(0 20px 8px rgb(104 112 118 / 0.04))",
    lg: "drop-shadow(0 12px 24px rgb(104 112 118 / 0.15)) drop-shadow(0 12px 14px rgb(104 112 118 / 0.1))",
    xl: "drop-shadow(0 25px 34px rgb(104 112 118 / 0.35))"
  }
};

// theme/dark.ts
var dark_default = {
  colors: {
    background: "$black",
    foreground: "$white",
    backgroundContrast: "$grey00",
    ...colors_exports,
    text: "$gray09",
    link: "$purple07",
    codeLight: "$purple05",
    code: "$purple06",
    selection: "$red08"
  },
  shadows: {
    xs: "0 2px 8px 1px rgb(0 0 0 / 0.07), 0 1px 1px -1px rgb(0 0 0 / 0.04)",
    sm: "0 2px 8px 2px rgb(0 0 0 / 0.07), 0 2px 4px -1px rgb(0 0 0 / 0.04)",
    md: "0 12px 20px 6px rgb(0 0 0 / 0.08)",
    lg: "0 12px 34px 6px rgb(0 0 0 / 0.18)",
    xl: "0 25px 65px 0px rgb(0 0 0 / 0.35)"
  },
  dropShadows: {
    xs: "drop-shadow(0 2px 4px rgb(0 0 0 / 0.07)) drop-shadow(0 1px 1px rgb(0 0 0 / 0.04))",
    sm: "drop-shadow(0 2px 8px rgb(0 0 0 / 0.07)) drop-shadow(0 2px 4px rgb(0 0 0 / 0.04))",
    md: "drop-shadow(0 4px 12px rgb(0 0 0 / 0.08)) drop-shadow(0 20px 8px rgb(0 0 0 / 0.04))",
    lg: "drop-shadow(0 12px 24px rgb(0 0 0 / 0.15)) drop-shadow(0 12px 14px rgb(0 0 0 / 0.1))",
    xl: "drop-shadow(0 25px 34px rgb(0 0 0 / 0.35))"
  }
};

// stitches.config.ts
var getStitchesTheme = (targetTheme) => {
  return deepMerge(targetTheme, common_default.theme);
};
var stitches = createStitches({
  ...common_default,
  theme: {
    ...common_default.theme,
    shadows: {
      ...light_default.shadows
    },
    dropShadows: {
      ...light_default.dropShadows
    },
    colors: {
      ...common_default.theme.colors,
      ...light_default.colors
    }
  }
});
var createThemeBase = stitches.createTheme;
var styled = stitches.styled;
var css = stitches.css;
var globalCss = stitches.globalCss;
var keyframes = stitches.keyframes;
var getCssText = stitches.getCssText;
var reset = stitches.reset;
var theme = stitches.theme;
var config = stitches.config;
var createTheme = ({ type, theme: theme2, className }) => {
  if (!type) {
    throw new Error("Theme type is required");
  }
  return createThemeBase(className || `${type}-theme`, deepMerge(type === "dark" ? dark_default : light_default, theme2));
};
var defaultContext = {
  isDark: false,
  theme,
  type: "light"
};
var ThemeContext = React4.createContext(defaultContext);
if (process.env.NODE_ENV !== "production") {
  ThemeContext.displayName = "AtomThemeContext";
}
var theming_default = ThemeContext;

// hooks/useTheme.ts
var useTheme = () => React4.useContext(theming_default);
var useTheme_default = useTheme;
var isBrowser = () => {
  return Boolean(typeof window !== "undefined" && window.document && window.document.createElement);
};
var useSSR = () => {
  const [browser, setBrowser] = useState(false);
  useEffect(() => {
    setBrowser(isBrowser());
  }, []);
  return {
    isBrowser: browser,
    isServer: !browser
  };
};
var useSSR_default = useSSR;
var getTokenValue = (token, tokenName) => {
  if (!document || !token)
    return "";
  let docStyle = getComputedStyle(document.documentElement);
  const tokenKey = `--${common_default.prefix}-${String(token)}-${tokenName}`;
  const tokenValue = docStyle.getPropertyValue(tokenKey);
  if (tokenValue && tokenValue.includes("var")) {
    getTokenValue(token, tokenValue);
  }
  return tokenValue;
};
var getDocumentCSSTokens = () => {
  const colorKeys = [
    ...Object.keys(common_default.theme.colors),
    ...Object.keys(light_default.colors)
  ];
  const shadowKeys = Object.keys(light_default.shadows);
  const colorTokens = colorKeys.reduce((acc, crr) => {
    const color = getTokenValue("colors", crr);
    if (color) {
      acc[crr] = {
        prefix: common_default.prefix,
        scale: "colors",
        token: crr,
        value: color
      };
    }
    return acc;
  }, {});
  const shadowTokens = shadowKeys.reduce((acc, crr) => {
    const shadow = getTokenValue("shadows", crr);
    if (shadow) {
      acc[crr] = {
        prefix: common_default.prefix,
        scale: "shadows",
        token: crr,
        value: shadow
      };
    }
    return acc;
  }, {});
  return {
    colors: colorTokens,
    shadows: shadowTokens
  };
};
var getThemeName = (theme2) => {
  if (typeof theme2 === "string") {
    return (theme2 == null ? void 0 : theme2.includes("-theme")) ? theme2 == null ? void 0 : theme2.replace("-theme", "") : theme2;
  }
  return theme2;
};
var changeTheme = (theme2) => {
  var _a, _b;
  if (!document)
    return;
  const el = document.documentElement;
  const prevClasses = ((_a = el == null ? void 0 : el.getAttribute("class")) == null ? void 0 : _a.split(" ").filter((cls) => !cls.includes("theme") && !cls.includes("light") && !cls.includes("dark"))) || [];
  const prevStyles = ((_b = el == null ? void 0 : el.getAttribute("style")) == null ? void 0 : _b.split(";").filter((stl) => !stl.includes("color-scheme") && stl.length).map((el2) => `${el2};`)) || [];
  el == null ? void 0 : el.setAttribute("class", clsx(prevClasses, `${getThemeName(theme2)}-theme`));
  el == null ? void 0 : el.setAttribute("style", clsx(prevStyles, `color-scheme: ${theme2};`));
};

// provider.tsx
var isObject = (target) => target && typeof target === "object";
var copyObject = (obj) => {
  if (!isObject(obj))
    return obj;
  if (obj instanceof Array)
    return [...obj];
  return { ...obj };
};
var AtomProvider = (props) => {
  const { children, theme: userTheme } = props;
  const { isBrowser: isBrowser2 } = useSSR_default();
  const [currentTheme, setCurrentTheme] = useState(defaultContext.type);
  const changeCurrentTheme = useCallback((type) => {
    setCurrentTheme((ct) => ct !== type ? type : ct);
  }, [setCurrentTheme]);
  const providerValue = useMemo(() => {
    const themeTokens = isBrowser2 ? getDocumentCSSTokens() : {};
    const theme2 = deepMerge(copyObject(defaultContext.theme), themeTokens);
    const themeName = getThemeName(currentTheme);
    return {
      theme: theme2,
      type: themeName,
      isDark: themeName === "dark"
    };
  }, [currentTheme, isBrowser2]);
  useEffect(() => {
    if (!isBrowser2 || !userTheme) {
      return;
    }
    if (userTheme == null ? void 0 : userTheme.className) {
      changeTheme(userTheme.className);
      changeCurrentTheme(getThemeName(userTheme.className));
    }
  }, [isBrowser2, userTheme, changeCurrentTheme]);
  return /* @__PURE__ */ React4.createElement(SSRProvider, null, /* @__PURE__ */ React4.createElement(OverlayProvider, null, /* @__PURE__ */ React4.createElement(theming_default.Provider, {
    value: providerValue
  }, children)));
};

export { AtomProvider, colors_exports as colors, config, createTheme, createThemeBase, css, getCssText, getStitchesTheme, globalCss, keyframes, reset, styled, theme, useSSR_default as useSSR, useTheme_default as useTheme };
